import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, User } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

// Publishing Navigation - reuses Goodman aesthetic
export const PublishingNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      data-testid="publishing-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link 
          to="/" 
          data-testid="pub-nav-logo"
          className="text-white font-heading text-2xl tracking-tight hover:opacity-80 transition-opacity"
        >
          GOODMAN
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <Link 
            to="/" 
            className="text-white/60 hover:text-white transition-colors text-sm font-body tracking-wide"
          >
            Home
          </Link>
          <Link 
            to="/books" 
            data-testid="pub-nav-books"
            className="text-white/90 hover:text-white transition-colors text-sm font-body tracking-wide"
          >
            Books
          </Link>
          {user ? (
            <Link 
              to="/my-library"
              data-testid="pub-nav-my-library"
              className="text-jcrown-gold hover:text-white transition-colors text-sm font-body tracking-wide flex items-center gap-1"
            >
              <User className="w-3.5 h-3.5" />
              My Library
            </Link>
          ) : (
            <Link 
              to="/login"
              data-testid="pub-nav-signin"
              className="text-white/60 hover:text-white transition-colors text-sm font-body tracking-wide"
            >
              Sign In
            </Link>
          )}
        </div>

        <div className="md:hidden flex items-center gap-4">
          <Link to="/books" className="text-white/70 text-sm font-body">Books</Link>
          {user ? (
            <Link to="/my-library" data-testid="pub-nav-my-library-mobile" className="text-jcrown-gold text-sm font-body">Library</Link>
          ) : (
            <Link to="/login" data-testid="pub-nav-signin-mobile" className="text-white text-sm font-body">Sign In</Link>
          )}
        </div>
      </div>
    </nav>
  );
};

// Publishing Footer - includes business inquiries
export const PublishingFooter = ({ onBusinessInquiryClick }) => {
  return (
    <footer 
      data-testid="publishing-footer"
      className="relative bg-primary border-t border-white/5 py-20 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Business inquiries strip */}
        <div className="border border-jcrown-gold/20 bg-black/40 p-8 md:p-12 mb-16">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="text-jcrown-gold/70 text-[10px] tracking-[0.3em] uppercase font-body mb-3">
                Goodman Publishing
              </p>
              <p className="font-body text-white/80 text-sm md:text-base tracking-wide">
                Publishing • Licensing • Film & Television • Adaptation • Brand Partnerships
              </p>
            </div>
            <button
              onClick={onBusinessInquiryClick}
              data-testid="business-inquiries-btn"
              className="group inline-flex items-center gap-2 border border-jcrown-gold/50 text-jcrown-gold px-6 py-3 font-body text-xs tracking-[0.2em] uppercase hover:bg-jcrown-gold hover:text-jcrown-dark transition-all active:scale-95"
            >
              Business Inquiries
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-6">
            <h2 className="font-heading text-white text-5xl md:text-6xl tracking-tighter uppercase mb-3">
              GOODMAN
            </h2>
            <p className="text-jcrown-gold/70 font-heading text-lg tracking-tight uppercase mb-4">
              Publishing
            </p>
            <p className="text-white/30 font-body text-sm max-w-sm">
              Original stories. Original characters. Original worlds. A growing catalog of intellectual property from Goodman Entertainment.
            </p>
          </div>
          
          <div className="md:col-span-3">
            <h3 className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-6 font-body">
              Library
            </h3>
            <ul className="space-y-4">
              <li><Link to="/books" className="text-white/60 hover:text-white transition-colors font-body text-sm">All Books</Link></li>
              <li><Link to="/books#complete-bundle" className="text-white/60 hover:text-white transition-colors font-body text-sm">Complete Collection</Link></li>
              <li><Link to="/books#pass" className="text-white/60 hover:text-white transition-colors font-body text-sm">Library Pass</Link></li>
            </ul>
          </div>
          
          <div className="md:col-span-3">
            <h3 className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-6 font-body">
              Goodman
            </h3>
            <ul className="space-y-4">
              <li><Link to="/" className="text-white/60 hover:text-white transition-colors font-body text-sm">Ecosystem</Link></li>
              <li><a href="/#worlds" className="text-white/60 hover:text-white transition-colors font-body text-sm">Brand Worlds</a></li>
              <li><a href="/#jcrown" className="text-white/60 hover:text-white transition-colors font-body text-sm">J.Crown</a></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-white/20 font-body text-xs">
            © 2026 Goodman Entertainment. All rights reserved.
          </p>
          <p className="text-white/20 font-body text-xs tracking-wider">
            THE JOHNNY CROWN LIBRARY™
          </p>
        </div>
      </div>
    </footer>
  );
};
